import { FC, useRef, useEffect, useCallback, useState } from 'react'
import { Send, Loader2, Lock, Paperclip, X, ImageIcon, Mic, Maximize2, Minimize2 } from 'lucide-react'
import { cn } from '../../../lib/utils'
import { useIsMobile } from '../../../hooks/useIsMobile'
import { useChat, formatToolDisplayName, type AgentToolCall, type ToolCallStatus } from '../../../contexts/ChatContext'
import { useWorkspace } from '../../../contexts/WorkspaceContext'
import { useAudioRecorder } from '../../../hooks/useAudioRecorder'
import { useTranscription } from '../../../hooks/useTranscription'
import { CasePanelMessage } from './CasePanelMessage'
import { StreamingProgressPanelView } from '../StreamingProgressPanel'
import { AgentSelector, AGENT_PERSONAS, getAgentPersona } from './AgentSelector'
import { loadCasePanelMode, saveCasePanelMode, type CasePanelMode } from './modeStorage'
import { useAutoScroll } from '../../../hooks/useAutoScroll'
import { uploadWorkspaceFile } from '../../../services/backendAdapter'
import { validateFile, getFileInputAccept } from '../../../utils/fileValidation'

interface CasePanelChatProps {
  className?: string
  workspaceId?: string
  onOpenCanvas?: (tab: string) => void
  onExpandToPanel?: (content: string, durationMs?: number) => void
}

/**
 * Map a per-agent AgentToolCall onto the ToolCallStatus shape StreamingProgressPanelView
 * renders, so the case panel's progress list is identical to the regular chat's.
 * displayName reuses the regular chat's formatter (skillLabel for synthetic rows).
 */
export const toToolCallStatus = (t: AgentToolCall, i: number): ToolCallStatus => ({
  id: t.callId || `${t.name}-${i}`,
  toolName: t.name,
  displayName: t.skillLabel || formatToolDisplayName(t.name),
  // A tool already in the array but without an explicit terminal signal (both
  // isRunning and success unset — e.g. seen before its tool_start event) is still
  // in progress, so it falls back to 'running', never a premature 'complete'.
  status: t.isRunning ? 'running' : t.success === true ? 'complete' : t.success === false ? 'error' : 'running',
  args: {
    query: typeof t.args?.query === 'string' ? t.args.query : undefined,
    skill_name: typeof t.args?.skill_name === 'string' ? t.args.skill_name : undefined,
  },
  citationCount: t.citationCount,
  startedAt: 0,
})

/**
 * Full case panel chat -- agent selector + message list + input bar.
 * Manages multiple simultaneous agent sessions via ChatContext.
 */
export const CasePanelChat: FC<CasePanelChatProps> = ({ className, workspaceId, onOpenCanvas, onExpandToPanel }) => {
  const {
    agentSessions,
    activeAgentId,
    switchAgent,
    sendToAgent,
    isAgentResponding,
    initAgentSession,
    transcribeAudio
  } = useChat()

  const {
    startRecording,
    stopRecording,
    isRecording,
    audioBlob,
    clearAudioBlob
  } = useAudioRecorder()

  const { hydrationData } = useWorkspace()
  const createdFromFlow = hydrationData?.workspace?.ui_state?.created_from_flow === true

  const [inputText, setInputText] = useState('')
  // Expanded composer: grow the textarea to fill the pane (mobile only).
  const isMobile = useIsMobile()
  const [composerExpanded, setComposerExpanded] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [transcriptionError, setTranscriptionError] = useState<string | null>(null)
  const [pendingFiles, setPendingFiles] = useState<Array<{ file_id: string; filename: string }>>([])
  // Edit vs Ask, seeded lazily from the 24h-TTL cache and scoped to workspaceId. Held
  // here (not in ChatContext) so it stays global across agent tabs for as long as the
  // case panel is mounted — this component does not remount when the active agent
  // changes, only when the workspace does (see the re-seed effect below).
  const [mode, setMode] = useState<CasePanelMode>(() => loadCasePanelMode(workspaceId ?? ''))
  // Re-seed the mode when the workspace identity changes (e.g. the physician opens a
  // different admission in the same mounted case panel) — a workspace the physician has
  // never set a mode in must start in Edit, never inherit whatever the previous
  // workspace was left on.
  useEffect(() => {
    setMode(loadCasePanelMode(workspaceId ?? ''))
  }, [workspaceId])

  const { messagesEndRef, scrollContainerRef, handleScroll } = useAutoScroll([agentSessions[activeAgentId]?.messages])
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const activeSession = agentSessions[activeAgentId]
  const activePersona = getAgentPersona(activeAgentId)

  const msgs = activeSession?.messages ?? []
  const lastMsg = msgs[msgs.length - 1]
  const isLoading = !!activeSession?.isLoading

  // Literally the same progress behavior as the regular chat (ChatMessages): the
  // streaming assistant turn is pulled out of the message list and represented by
  // StreamingProgressPanelView — Preparing (no signal yet) or Working (a tool is
  // running) — until the bubble has something to show. The instant that happens the
  // panel disappears and the message bubble renders it; "Writing" IS the streaming
  // text, not a pill. Reasoning is NOT a panel phase: a reasoning-only turn is kept
  // in the list so CasePanelMessage's live, auto-expanded ThinkingPanel shows the
  // streaming reasoning text (see hasStreamingContent below).
  const isStreamingAssistant = isLoading && lastMsg?.role === 'assistant'
  // "Something to show" is prose OR an inline canvas update. A canvas_update SSE event can
  // arrive before the first text_delta (HPI/AP-only turns); gating on prose alone would slice
  // a canvas-bearing message out of the visible list and hide its InlineCanvas until the turn
  // completes (#196). Count a canvas update as bubble content so the panel gives way and
  // CasePanelMessage renders the canvas as it streams.
  const hasCanvasUpdate = (lastMsg?.canvasUpdates?.length ?? 0) > 0
  // Also un-slice once reasoning text exists (thinkingContent is set — '' included — from the
  // first thinking event). Pre-prose reasoning is live streaming the physician must SEE: keeping
  // the turn in the list lets CasePanelMessage render its auto-expanding ThinkingPanel
  // (isStreaming = message.isThinking) with no empty bubble, instead of a mute "Reasoning" pill
  // that hid the actual reasoning text. Gating on thinkingContent (presence) rather than the
  // transient isThinking flag keeps the panel mounted across the reasoning→tool→prose handoff
  // (isThinking flips false between phases while thinkingContent persists), so it never flickers
  // back to a Preparing pill in the gap before the first prose token. The regular chat now wires
  // the same isThinking/isStreaming signal through to its ThinkingPanel — this is parity, not a
  // case-panel-only divergence. (canvasUpdate stays in the gate for the same reason: InlineCanvas
  // streams before prose on HPI/AP-only turns.)
  const hasStreamingContent = isStreamingAssistant && (!!lastMsg?.content || hasCanvasUpdate || lastMsg?.thinkingContent !== undefined)
  // The panel stands in for the streaming turn only while the bubble has nothing to show yet.
  const showProgress = isLoading && !hasStreamingContent
  const streamingMsg = isStreamingAssistant && !hasStreamingContent ? lastMsg : undefined

  const visibleMessages = (streamingMsg ? msgs.slice(0, -1) : msgs)
    .filter(msg => !(msg.fromPipeline && msg.role === 'user'))

  // Feed the shared panel from the active agent's streaming message. `isStreaming`
  // is held false: the panel is unmounted the moment the bubble has something to show,
  // so it only ever labels Preparing or Working — never a premature "Writing", and no
  // longer "Reasoning" (a reasoning-only turn is un-sliced above and shows its live
  // ThinkingPanel in the bubble instead). isThinking is still passed through for the
  // unreachable isThinking-without-thinkingContent edge, matching the regular chat's
  // StreamingProgressPanel phase precedence.
  const progressData = {
    toolCalls: (streamingMsg?.tools ?? []).map(toToolCallStatus),
    isThinking: streamingMsg?.isThinking === true,
    isGenerating: showProgress,
    isStreaming: false,
  }

  // Initialize casey session on first mount if not present
  useEffect(() => {
    if (!agentSessions['casey']) {
      initAgentSession('casey', "Hi! I'm Casey -- your attending hospitalist. I manage the patient panel, coordinate evidence, and handle quality reviews. Ask me anything about the patient or the case.")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Handle send — block while agent is already responding.
  // Gate on BOTH isLoading AND isAgentResponding: isLoading is false for hidden
  // (pipeline/relay) turns, but those still hold the in-flight abort controller, so
  // sendToAgent would refuse this turn. Without the isAgentResponding check we would
  // clear the composer below for a send that never happened — silently dropping the
  // physician's typed message and attachments. Checking the guard's own condition
  // first keeps the input intact when a turn is already in flight. The no-op leaves
  // the composer untouched rather than surfacing an error: the only currently-reachable
  // in-flight state is a visible turn, which already disables the Send button via
  // isLoading, so there is nothing to signal for the (unreachable) hidden-turn case.
  const handleSend = useCallback(() => {
    if (!inputText.trim()) return
    if (activeSession?.isLoading || isAgentResponding(activeAgentId)) return
    const fileIds = pendingFiles.map(f => f.file_id).filter(Boolean)
    sendToAgent(activeAgentId, inputText, mode, undefined, { fileIds: fileIds.length > 0 ? fileIds : undefined })
    setInputText('')
    setPendingFiles([])
    setComposerExpanded(false)
    // Refocus textarea
    textareaRef.current?.focus()
  }, [inputText, activeAgentId, sendToAgent, isAgentResponding, pendingFiles, activeSession?.isLoading, mode])

  // Handle keyboard
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }, [handleSend])

  // Auto-resize textarea — skipped when expanded, where flex sizing fills the pane.
  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    if (composerExpanded) {
      el.style.height = ''
      return
    }
    el.style.height = 'auto'
    el.style.height = Math.min(el.scrollHeight, 120) + 'px'
  }, [inputText, composerExpanded])

  // The expanded composer is a mobile affordance; never leave it stuck on desktop.
  useEffect(() => {
    if (!isMobile) setComposerExpanded(false)
  }, [isMobile])

  // Process transcribed audio → append to input, surface failures inline
  useTranscription({
    audioBlob,
    clearAudioBlob,
    transcribeAudio,
    onText: text => setInputText(prev => (prev ? prev + ' ' : '') + text),
    onError: setTranscriptionError,
  })

  // Clear any prior transcription error, then start a fresh recording
  const handleStartRecording = useCallback(() => {
    setTranscriptionError(null)
    startRecording()
  }, [startRecording])

  // Handle file attachment selection — surfaces errors to user
  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files || [])
    if (selected.length === 0 || !workspaceId) return

    setIsUploading(true)
    setUploadError(null)
    const rejected: string[] = []
    try {
      for (const file of selected) {
        const validation = validateFile(file)
        if (!validation.valid) {
          console.error(`[CasePanelChat] File rejected: ${file.name} — ${validation.error}`)
          rejected.push(`${file.name}: ${validation.error}`)
          continue
        }
        const result = await uploadWorkspaceFile(workspaceId, file)
        if (result?.file_id) {
          const fileId = result.file_id
          setPendingFiles(prev => [...prev, { file_id: fileId, filename: file.name }])
        }
      }
      if (rejected.length > 0) {
        setUploadError(rejected.join('; '))
        setTimeout(() => setUploadError(null), 5000)
      }
    } catch (err) {
      console.error('[CasePanelChat] File upload failed:', err)
      setUploadError('File upload failed — check connection and try again')
      setTimeout(() => setUploadError(null), 5000)
    } finally {
      setIsUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }, [workspaceId])

  // Edit a user message: pre-fill the composer with its text and focus it.
  // Frontend-only (composer-repopulate); no server truncation — the original
  // turn remains. True truncating rewind is deferred (needs per-message seq).
  // Edit is a text-only rewind, so drop any pending attachments (they belonged to
  // the in-progress draft, not this message) — mirrors handleAgentSelect's reconcile.
  // Without this the composer would show the edited text alongside stale attachments.
  const handleEditMessage = useCallback((messageId: string) => {
    const msg = activeSession?.messages.find(m => m.id === messageId)
    if (!msg || msg.role !== 'user') return
    setInputText(msg.content)
    setPendingFiles([])
    setComposerExpanded(false)
    textareaRef.current?.focus()
  }, [activeSession?.messages])

  // Lazy-init session when switching to an agent without one
  // Clear pending files since they're contextual to the previous agent
  const handleAgentSelect = useCallback((agentId: string) => {
    if (!agentSessions[agentId]) {
      const persona = getAgentPersona(agentId)
      initAgentSession(agentId, `Hi! I'm ${persona.name}. How can I help?`)
    }
    setPendingFiles([])
    switchAgent(agentId)
  }, [agentSessions, switchAgent, initAgentSession])

  const handleModeSelect = useCallback((next: CasePanelMode) => {
    setMode(next)
    saveCasePanelMode(workspaceId ?? '', next)
  }, [workspaceId])

  // Composer controls — shared between the collapsed row and the expanded toolbar.
  const attachButton = (
    <button
      type="button"
      onClick={() => fileInputRef.current?.click()}
      disabled={!workspaceId || isUploading}
      className={cn(
        'flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center',
        'text-muted-foreground hover:text-foreground hover:bg-muted transition-colors',
        'disabled:opacity-50 disabled:cursor-not-allowed'
      )}
      title={!workspaceId ? 'No workspace — cannot attach files' : 'Attach file'}
    >
      {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
    </button>
  )

  const micButton = (
    <button
      type="button"
      onClick={isRecording ? stopRecording : handleStartRecording}
      className={cn(
        'flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center transition-colors',
        isRecording
          ? 'bg-destructive text-destructive-foreground animate-pulse'
          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
      )}
      title={isRecording ? 'Stop recording' : 'Voice input'}
    >
      {isRecording ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mic className="w-4 h-4" />}
    </button>
  )

  const expandButton = (
    <button
      type="button"
      onClick={() => setComposerExpanded(v => !v)}
      className={cn(
        'flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center',
        'text-muted-foreground hover:text-foreground hover:bg-muted transition-colors'
      )}
      title={composerExpanded ? 'Collapse message box' : 'Expand message box'}
      aria-label={composerExpanded ? 'Collapse message box' : 'Expand message box'}
    >
      {composerExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
    </button>
  )

  const sendButton = (
    <button
      onClick={handleSend}
      // Mirror handleSend's full guard so the button's affordance matches its behavior:
      // isLoading is false for hidden (pipeline/relay) turns, but those still hold the
      // in-flight controller, so a send would no-op. Disabling on isAgentResponding too
      // means the button is never enabled-but-dead.
      disabled={!inputText.trim() || activeSession?.isLoading || isAgentResponding(activeAgentId)}
      className={cn(
        'flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center',
        'bg-primary text-primary-foreground',
        'hover:bg-primary/90 transition-colors',
        'disabled:opacity-50 disabled:cursor-not-allowed'
      )}
    >
      {activeSession?.isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
    </button>
  )

  // Segmented Edit/Ask pill, styled after AgentSelector's tab group so the two
  // controls in this pane read as one system. Ask routes to the backend's read-only
  // mode: it filters every mutating tool and injects a no-writes constraint.
  const modeControl = (
    <div
      role="group"
      aria-label="Agent mode"
      className="flex items-center gap-1 mb-2 w-fit flex-shrink-0 bg-muted/50 rounded-lg p-0.5 border border-border/50"
    >
      {(['edit', 'ask'] as const).map(m => (
        <button
          key={m}
          type="button"
          onClick={() => handleModeSelect(m)}
          aria-pressed={mode === m}
          title={m === 'edit' ? 'Edit — the agent can update the canvases' : 'Ask — read only, no canvas changes'}
          className={cn(
            'px-2 py-0.5 text-[11px] font-medium rounded-md transition-all duration-200',
            mode === m
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
          )}
        >
          {m === 'edit' ? 'Edit' : 'Ask'}
        </button>
      ))}
    </div>
  )

  return (
    <div className={cn('flex flex-col h-full bg-background', className)}>
      {/* Header with agent selector */}
      <div className="flex items-center justify-center flex-shrink-0 px-4 py-2 border-b border-border bg-background/95 backdrop-blur-sm">
        {Object.keys(AGENT_PERSONAS).length > 1 ? (
          <AgentSelector
            agents={AGENT_PERSONAS}
            activeAgentId={activeAgentId}
            onSelect={handleAgentSelect}
            hideFlowChat={!createdFromFlow}
            flowChatReadOnly={createdFromFlow}
          />
        ) : (
          <div className="flex items-center gap-2 text-sm font-semibold">
            <span
              className={cn(
                'w-5 h-5 rounded-full bg-gradient-to-br flex items-center justify-center text-[9px] font-bold text-white',
                activePersona.accentFrom, activePersona.accentTo
              )}
            >
              {activePersona.avatar}
            </span>
            {activePersona.name}
          </div>
        )}
      </div>

      {/* Flow chat read-only banner */}
      {activeAgentId === 'flow-chat' && (
        <div className="flex items-center gap-2 px-4 py-1.5 text-xs text-sky-700 dark:text-sky-300 bg-sky-50 dark:bg-sky-950/30 border-b border-sky-200 dark:border-sky-800/50">
          <Lock className="w-3.5 h-3.5 flex-shrink-0" />
          <span>Flow chat history — read only</span>
        </div>
      )}

      {/* Message list */}
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className={cn(
          // scrollbar-gutter:stable reserves the scrollbar's track width whether or
          // not the bar is drawn, so the message list's content width never changes
          // as content crosses the viewport-height boundary during streaming. Without
          // it, the layout-consuming scrollbar appearing/disappearing re-wraps text
          // and feeds a bistable flicker (the "scrollbar glitches in and out" bug).
          'flex-1 overflow-y-auto [scrollbar-gutter:stable] px-4 py-4 space-y-1',
          composerExpanded && 'hidden'
        )}
      >
        {visibleMessages.map(msg => (
          <CasePanelMessage
            key={msg.id}
            message={msg}
            agentId={activeAgentId}
            onOpenCanvas={onOpenCanvas}
            onExpandToPanel={onExpandToPanel}
            isLoading={activeSession?.isLoading}
            onEdit={
              activeAgentId !== 'flow-chat' && msg.role === 'user' && !msg.fromPipeline
                ? () => handleEditMessage(msg.id)
                : undefined
            }
          />
        ))}

        {/* Progress panel: the regular chat's StreamingProgressPanel, fed from the
            active agent's streaming turn. Shown only while no prose has arrived
            (Preparing/Reasoning/Working); it disappears the instant content streams,
            handing off to the message bubble. Keyed per agent so it never carries
            visual state across agent tabs. */}
        {showProgress && (
          <StreamingProgressPanelView key={activeAgentId} {...progressData} />
        )}

        {/* Error indicator */}
        {activeSession?.error && !activeSession?.isLoading && (
          <div className="flex justify-center mb-3 animate-in fade-in-0">
            <div className="text-xs text-muted-foreground bg-destructive/5 border border-destructive/20 rounded-full px-4 py-1.5">
              {activeSession.error.includes('fetch') || activeSession.error.includes('network') || activeSession.error.includes('Failed to fetch')
                ? 'Connection issue -- check that the backend is running, then try again.'
                : activeSession.error}
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input bar -- hidden when viewing read-only flow chat.
          Expanded (mobile): the bar becomes a flex column that fills the pane up
          to the agent header, the textarea grows into it, and the controls drop
          to a toolbar below. Collapsed: the classic single row. */}
      {activeAgentId !== 'flow-chat' && <div className={cn(
        'px-4 py-3 border-t border-border bg-background/95',
        composerExpanded && 'flex flex-col flex-1 min-h-0 gap-2'
      )}>
        {modeControl}
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={getFileInputAccept()}
          className="hidden"
          onChange={handleFileSelect}
        />
        <div className={cn('flex gap-2', composerExpanded ? 'flex-col flex-1 min-h-0' : 'items-end')}>
          {!composerExpanded && attachButton}
          {!composerExpanded && micButton}
          <textarea
            ref={textareaRef}
            value={inputText}
            onChange={e => {
              setInputText(e.target.value)
              if (transcriptionError) setTranscriptionError(null)
            }}
            onKeyDown={handleKeyDown}
            placeholder={
              isRecording
                ? 'Listening...'
                : activeSession?.isLoading
                  ? `${activePersona.name} is working on a response...`
                  : `Message ${activePersona.name}...`
            }
            disabled={false}
            rows={1}
            className={cn(
              'flex-1 resize-none rounded-xl border border-border bg-muted px-4 py-2.5',
              'text-sm text-foreground placeholder:text-muted-foreground',
              'focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              composerExpanded ? 'min-h-0 max-h-none' : 'min-h-[40px] max-h-[120px]'
            )}
          />
          {!composerExpanded && isMobile && expandButton}
          {!composerExpanded && sendButton}
          {/* Expanded toolbar: attach/mic left, collapse + send right */}
          {composerExpanded && (
            <div className="flex items-center gap-2 flex-shrink-0">
              {attachButton}
              {micButton}
              <div className="flex-1" />
              {expandButton}
              {sendButton}
            </div>
          )}
        </div>
        {/* File upload error */}
        {uploadError && (
          <div className="px-4 py-1.5 text-xs text-destructive bg-destructive/5 border-t border-destructive/20">
            {uploadError}
          </div>
        )}
        {/* Transcription error */}
        {transcriptionError && (
          <div className="px-4 py-1.5 text-xs text-destructive bg-destructive/5 border-t border-destructive/20">
            {transcriptionError}
          </div>
        )}
        {/* File preview chips */}
        {pendingFiles.length > 0 && (
          <div className="flex flex-wrap gap-1.5 px-4 py-1">
            {pendingFiles.map(f => {
              const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(f.filename)
              return (
                <span
                  key={f.file_id}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted border border-border text-xs text-foreground/80"
                >
                  {isImage
                    ? <ImageIcon className="h-3 w-3 text-muted-foreground" />
                    : <Paperclip className="h-3 w-3 text-muted-foreground" />
                  }
                  <span className="truncate max-w-[120px]">{f.filename}</span>
                  <button
                    onClick={() => setPendingFiles(prev => prev.filter(p => p.file_id !== f.file_id))}
                    className="text-muted-foreground hover:text-foreground ml-0.5"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              )
            })}
          </div>
        )}
      </div>}
    </div>
  )
}
